import {
    Autocomplete,
    Avatar,
    CircularProgress,
    Stack,
    SxProps,
    TextField,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement, SyntheticEvent, useState } from "react"

import useDnoUserSuggestion from "../../../services/middleware/hooks/dnoInterface/protected/useDnoUserSuggestion"
import useGetAssigneeById, {
    User,
} from "../../../services/middleware/hooks/dnoInterface/protected/useGetAssigneeById"
import { ITEM_BORDER_MULTIPLIER } from "../../../themes/defaultThemeParams"
import DisplayIfElse from "../../wrappers/displayIfElse"

const AVATAR_HEIGHT = 30
const AVATAR_WIDTH = 30
const AVATAR_FONT_SIZE = "12px"

type TUserSuggestionAutocompleteProps = {
    userId?: string | undefined
    filter?: boolean
    assignedTo?: string | null
    sx?: SxProps
    onSelectedUserChange: (
        newValue: User | null,
        resetOnFail: () => void
    ) => void
}

/**
 * Controlled Autocomplete component for user suggestions and selection
 */
const UserSuggestionAutocomplete = ({
    userId,
    filter,
    sx = {},
    onSelectedUserChange,
}: TUserSuggestionAutocompleteProps): ReactElement => {
    const theme = useTheme()
    // Autocomplete state
    const [value, setValue] = useState<User | null>(null)
    const [input, setInput] = useState("")

    // Get user by ID
    const { isGetByIdLoading } = useGetAssigneeById(setValue, userId || "")

    // Fetch user suggestions
    const { users, isLoadingSuggestion } = useDnoUserSuggestion(input)

    // Autocomplete handlers
    const handleInputChange = (
        event: SyntheticEvent<Element, Event>,
        newInputValue: string
    ): void => {
        setInput(newInputValue)
    }

    // This gets called when selecting a value from the dropdown
    const handleAutocompleteChange = (
        event: SyntheticEvent<Element, Event>,
        newValue: User | null
    ): void => {
        setValue(newValue || null)
        onSelectedUserChange(newValue || null, () => setValue(value))
    }

    const avatarColour = (user: User | null): string => {
        if (user?.username === "Resolved") {
            return theme.palette.success.main
        }
        if (user?.username) {
            return theme.palette.primary.main
        }
        if (value || null) {
            return theme.palette.primary.main
        }
        return theme.palette.grey[400]
    }

    return (
        <Autocomplete
            value={value}
            inputValue={input}
            onChange={handleAutocompleteChange}
            onInputChange={handleInputChange}
            options={users || []}
            loading={isLoadingSuggestion || isGetByIdLoading}
            groupBy={users ? (option) => option.group : undefined}
            getOptionLabel={(option) => String(option.name)}
            isOptionEqualToValue={(option) =>
                option?.username === value?.username
            }
            renderOption={(props, option: User) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...props}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                            sx={{
                                bgcolor: avatarColour(option),
                                width: AVATAR_WIDTH,
                                height: AVATAR_HEIGHT,
                                fontSize: AVATAR_FONT_SIZE,
                            }}
                        >
                            {option.initials}
                        </Avatar>
                        <Typography paddingLeft="5px" variant="body2">
                            {option.name}
                        </Typography>
                    </Stack>
                </li>
            )}
            sx={{
                width: "100%",
                ...sx,
            }}
            renderInput={(params) => {
                const selectedUser: User | null | undefined = value
                return (
                    <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        placeholder={filter ? "All Users" : "Unassigned"}
                        InputProps={{
                            ...params.InputProps,
                            sx: {
                                borderRadius: ITEM_BORDER_MULTIPLIER,
                            },
                            startAdornment: (
                                <DisplayIfElse
                                    condition={
                                        isLoadingSuggestion || isGetByIdLoading
                                    }
                                    displayIfTrue={
                                        <CircularProgress
                                            color="inherit"
                                            size={30}
                                        />
                                    }
                                    displayIfFalse={
                                        <Avatar
                                            sx={{
                                                bgcolor:
                                                    avatarColour(selectedUser),
                                                width: AVATAR_WIDTH,
                                                height: AVATAR_HEIGHT,
                                                fontSize: AVATAR_FONT_SIZE,
                                                marginRight: 1,
                                            }}
                                        >
                                            {selectedUser
                                                ? selectedUser.initials
                                                : null}
                                        </Avatar>
                                    }
                                />
                            ),
                        }}
                    />
                )
            }}
        />
    )
}

UserSuggestionAutocomplete.defaultProps = {
    sx: {},
    assignedTo: "",
    filter: false,
    userId: "",
}
export default UserSuggestionAutocomplete
